import React, { useEffect, useState } from 'react';
import feed from '../content/feed.json';
import { Section, Eyebrow, SectionHead, Button } from './ui';

/**
 * Latest. The Substack feed (fetched at build time into src/content/feed.json)
 * merged with /latest.json (written by Forge, pushed by Srini), newest first.
 */

const KIND_LABEL = { post: 'Substack', linkedin: 'LinkedIn', article: 'Substack', talk: 'Speaking', book: 'Book', press: 'Press' };

const formatDate = (iso) => {
  if (!iso) return '';
  const d = new Date(`${iso}T00:00:00`);
  return isNaN(d) ? '' : d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
};

export const useLatest = (limit = 5) => {
  const [items, setItems] = useState(() => (feed.items || []).slice(0, limit));
  useEffect(() => {
    let cancelled = false;
    fetch('/latest.json')
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (cancelled) return;
        const extra = data && Array.isArray(data.items)
          ? data.items.map((i) => ({ kind: i.kind === 'post' ? 'linkedin' : i.kind, title: i.text, link: i.link, date: i.date }))
          : [];
        const merged = [...extra, ...(feed.items || [])]
          .filter((i) => i.title && i.link)
          .sort((a, b) => (b.date || '').localeCompare(a.date || ''))
          .slice(0, limit);
        setItems(merged);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [limit]);
  return items;
};

export const LatestList = ({ items }) => (
  <ul className="divide-y divide-navy-200 dark:divide-navy-700 border-y border-navy-200 dark:border-navy-700">
    {items.map((i) => (
      <li key={i.link + i.title}>
        <a href={i.link} target="_blank" rel="noopener noreferrer"
          className="grid grid-cols-1 md:grid-cols-12 gap-1 md:gap-6 py-4 group">
          <span className="md:col-span-2 font-sans text-label uppercase font-semibold text-emerald-600 dark:text-emerald-300 self-center">
            {KIND_LABEL[i.kind] || 'Note'}
          </span>
          <span className="md:col-span-7 font-sans text-[17px] font-medium text-navy-800 dark:text-white group-hover:text-emerald-600 dark:group-hover:text-emerald-300">
            {i.title}
          </span>
          <span className="md:col-span-3 font-mono text-meta text-navy-500 dark:text-navy-400 md:text-right self-center">
            {formatDate(i.date)}
          </span>
        </a>
      </li>
    ))}
  </ul>
);

const Latest = ({ limit = 5 }) => {
  const items = useLatest(limit);
  if (!items.length) return null;
  return (
    <Section tone="page" id="latest">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <Eyebrow>Latest</Eyebrow>
          <SectionHead>What we are writing and saying.</SectionHead>
        </div>
        <Button to="/what-we-think" variant="ghost">All writing and speaking</Button>
      </div>
      <LatestList items={items} />
    </Section>
  );
};

export default Latest;
